import { useCallback, useRef, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog"

function resolveBoolean(
  value: boolean | string | undefined,
  resolveSymbol: (value: string) => unknown,
  fallback: boolean,
): boolean {
  if (value === undefined) return fallback
  if (typeof value === "boolean") return value

  const resolved = resolveSymbol(value)
  if (typeof resolved === "boolean") return resolved
  return String(resolved ?? value).trim().toLowerCase() === "true"
}

export function DialogRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: Dialog root is a portal-emitting provider;
  // the content may not be in the DOM when closed. Surface atom contract
  // on a thin wrapping span that is always in the tree.
  // Note: `element.variant === "open"` is a legacy open-state marker.
  const atomDefaults = useAtomDefaults("dialog")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const hasCanonicalOpen = element.open !== undefined
  const canonicalOpen = resolveBoolean(element.open, resolveSymbol, false)
  const authoredOpen = element.variant === "open" || canonicalOpen
  // defaultOpen: uncontrolled force-open (preview / initial-open use cases).
  // Matches alert-dialog-renderer. Orthogonal to variant="open" which is a
  // controlled open-state marker that collides with matrix variant overrides
  // in DS Browser previews.
  const defaultOpen = resolveBoolean(element.defaultOpen, resolveSymbol, false)
  const hasAuthoredOpen = element.variant === "open"
    || hasCanonicalOpen
    || element.defaultOpen !== undefined
  const requestedOpen = element.variant === "open" || hasCanonicalOpen
    ? authoredOpen
    : defaultOpen
  const [isOpen, setIsOpen] = useState(requestedOpen)
  const previousElementRef = useRef(element)

  if (previousElementRef.current !== element && hasAuthoredOpen) {
    previousElementRef.current = element
    if (isOpen !== requestedOpen) setIsOpen(requestedOpen)
  }
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  const canonicalTitle = element.title
    ? String(resolveSymbol(element.title) ?? element.title)
    : undefined

  // Type-based: find trigger/content by type, fallback to flat children for legacy
  // JITRULE Task 2: the found trigger/content children may nest their
  // fields under `props`.
  const allChildren = element.children ?? []
  const rawTriggerChild = allChildren.find(c => c.type === "dialog-trigger")
  const triggerChild = rawTriggerChild ? liftV4Props(rawTriggerChild) : undefined
  const rawContentWrapper = allChildren.find(c => c.type === "dialog-content")
  const contentWrapper = rawContentWrapper ? liftV4Props(rawContentWrapper) : undefined
  const dialogBody = contentWrapper?.children
    ?? allChildren.filter((child) => child !== triggerChild && child !== contentWrapper)
  const dialogTitle = canonicalTitle
    ?? (contentWrapper?.text
      ? String(resolveSymbol(contentWrapper.text) ?? "")
      : text)
  const dialogClassName = contentWrapper?.className
  const triggerClassName = triggerChild?.className
  const triggerContent = triggerChild?.children?.length
    ? triggerChild.children.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))
    : triggerChild?.text
      ? String(resolveSymbol(triggerChild.text) ?? "")
      : null

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (!open) {
        dispatchEvent(element.id, "close", {})
      } else {
        dispatchEvent(element.id, "open", {})
      }
    },
    [element.id, dispatchEvent],
  )

  return (
    <span
      data-atom-type="dialog"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-is-open={isOpen ? "true" : "false"}
      data-title={dialogTitle}
      data-testid={`dialog-${element.id}`}
      aria-expanded={isOpen}
      aria-label={dialogTitle}
      className={element.className ? cn("inline-block", element.className) : undefined}
    >
      <Dialog
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        {triggerChild && (
          <DialogTrigger
            className={cn(stripPresetConflicts(
              triggerClassName,
              undefined,
              element.designAuthorityClassName,
            ))}
            data-testid={`dialog-trigger-${element.id}`}
            data-cogo-element-id={triggerChild.id}
            data-cogo-type={triggerChild.type}
          >
            {triggerContent}
          </DialogTrigger>
        )}
        <DialogContent
          className={cn(
            stripPresetConflicts(
              dialogClassName ?? element.className,
              undefined,
              element.designAuthorityClassName,
            ),
          )}
          data-testid={`dialog-content-${element.id}`}
          data-cogo-element-id={contentWrapper?.id ?? `${element.id}__content`}
          data-cogo-type="dialog-content"
        >
          {dialogTitle && (
            <DialogTitle>
              {dialogTitle}
            </DialogTitle>
          )}
          {dialogBody.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </DialogContent>
      </Dialog>
    </span>
  )
}
